import { Diagnostic } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DiagnosticPicker } from "./DiagnosticPicker";
import AddInIds from "@/constants/AddInIds";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { CatalogDiagnostic } from "@/hooks/diagnostic/useDiagnosticCatalog";
import diagnosticsToPreset from "@/hooks/presets/diagnosticsToPreset";
import { Preset } from "@/models/Preset";

export const PresetEditor: React.FC<{
  preset: Partial<Preset> | null;
  onClose: () => void;
  isNew: boolean;
}> = ({ preset, onClose, isNew }) => {
  const { t } = useTranslation();
  const updateAddIn = useUpdateAddIn<Preset>(AddInIds.presets, "id");

  const [description, setDescription] = useState(preset?.description || "");
  const [selectedDiagnostics, setSelectedDiagnostics] = useState<CatalogDiagnostic[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (preset?.columns) {
      setSelectedDiagnostics(
        preset.columns.map(col => ({
          id: col.diagnostic.id,
          name: col.diagnostic.name || col.diagnostic.id,
        })),
      );
      setDescription(preset.description || "");
    }
  }, [preset]);

  const handleSavePreset = async () => {
    if (!description.trim()) {
      alert(t("presetEditor.descriptionRequired"));
      return;
    }

    setIsSaving(true);
    try {
      await updateAddIn.mutateAsync(
        diagnosticsToPreset(
          description,
          selectedDiagnostics.map(diag => ({ id: diag.id, name: diag.name }) as Diagnostic),
          preset?.groups?.[0]?.id || "",
        ),
      );
      onClose();
    } catch (error) {
      console.error("Failed to save preset:", error);
      alert(t("presetEditor.saveFailed"));
    } finally {
      setTimeout(() => setIsSaving(false), 2000);
    }
  };

  const removeDiagnostic = (id: string) =>
    setSelectedDiagnostics(current => current.filter(diagnostic => diagnostic.id !== id));

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant={"h6"} gutterBottom>
        {isNew ? t("presetEditor.addTitle") : t("presetEditor.editTitle")}
      </Typography>
      <TextField
        label={t("presetEditor.description")}
        fullWidth
        value={description}
        onChange={e => setDescription(e.target.value)}
        sx={{ mb: 2 }}
        required
      />

      <Typography variant={"subtitle1"}>
        {t("presetEditor.selected", { count: selectedDiagnostics.length })}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, maxHeight: 160, overflowY: "auto" }}>
        {selectedDiagnostics.map(diagnostic => (
          <Chip
            key={diagnostic.id}
            label={diagnostic.name}
            title={diagnostic.id}
            onDelete={() => removeDiagnostic(diagnostic.id)}
            size={"small"}
          />
        ))}
      </Box>

      <DiagnosticPicker selected={selectedDiagnostics} onChange={setSelectedDiagnostics} />

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button
          variant={"contained"}
          onClick={handleSavePreset}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {t("save")}
        </Button>
        <Button variant={"outlined"} onClick={onClose}>
          {t("presetEditor.cancel")}
        </Button>
      </Box>
    </Box>
  );
};
